<script setup lang="ts">
import { ref } from 'vue'
import { BrokenPartsConfiguration, BuybackStrategy } from '@/types'
import { buybackStrategiesApi } from '@/api/client'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/UserStore'
import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Info } from 'lucide-vue-next'

const user = useUserStore()

const { t } = useI18n()
const props = defineProps<{
    strategy: BuybackStrategy
}>()

const emit = defineEmits(['success', 'error'])

const strategy = ref(props.strategy)

let absPricePositioning: number = Math.abs(strategy.value.price_positioning)
const greaterOrLesser = ref(strategy.value.price_positioning > 0 ? 'greater' : 'lesser')
/** Legacy `euro` rows round like `fixed`, and are saved as `fixed` from here on. */
const roundingType = ref(strategy.value.rounding_type === 'exact_amount' ? 'exact_amount' : 'fixed')

const isVariation = strategy.value.type === 'variation' || !strategy.value.type

const isToggling = ref(false)

/**
 * A copy, so edits stay local until they are applied. Pre-filled with the config
 * defaults for a customer who never saved; the first save writes every part.
 */
const brokenParts = ref<BrokenPartsConfiguration>(JSON.parse(JSON.stringify(strategy.value.broken_parts_configuration)))
const hasBrokenParts = Object.keys(brokenParts.value.deductions).length > 0
const minimumTradeIn = ref<string | number>(brokenParts.value.minimum_trade_in ?? '')
const zeroOutThreshold = ref<string | number>(brokenParts.value.zero_out_threshold ?? '')

/** An empty floor input is sent as null, which falls back to the default. */
const toNullableNumber = (value: string | number): number | null => (value === '' ? null : Number(value))

/** Numbers go as typed: the server validates them and its cast stores them typed. */
const brokenPartsValues = () => ({
    deductions: brokenParts.value.deductions,
    minimum_trade_in: toNullableNumber(minimumTradeIn.value),
    zero_out_threshold: toNullableNumber(zeroOutThreshold.value),
})

/**
 * A disabled strategy keeps its configuration — it is simply never applied, so the
 * suggested price stays the raw market figure until it is switched back on.
 */
const onToggle = async (enabled: boolean) => {
    isToggling.value = true

    try {
        const response = await buybackStrategiesApi.toggle(strategy.value.category.id, enabled)

        strategy.value.enabled = enabled
        emit('success', response.data as Array<BuybackStrategy>)
    } catch (error: any) {
        emit('error', error.response.data.message)
    } finally {
        isToggling.value = false
    }
}

const onSubmit = async (_event: Event) => {
    try {
        const response = await buybackStrategiesApi.update(strategy.value.category.id, {
            type: strategy.value.type ?? 'variation',
            rounding_value: strategy.value.rounding_value,
            rounding_type: roundingType.value,
            price_positioning: greaterOrLesser.value === 'greater' ? absPricePositioning : -absPricePositioning,
            ...(hasBrokenParts ? { values: brokenPartsValues() } : {}),
        })

        emit('success', response.data as Array<BuybackStrategy>)
    } catch (error: any) {
        emit('error', error.response.data.message)
    }
}
</script>

<template>
    <AccordionItem :value="strategy.id.toString()">
        <div class="flex items-center gap-4">
            <AccordionTrigger class="text-base" :class="{ 'text-muted-foreground': !strategy.enabled }">
                {{ t('strategies.buyback.card.title', { category: strategy.category.name }) }}
            </AccordionTrigger>
            <Switch
                :model-value="strategy.enabled"
                :disabled="isToggling"
                :aria-label="t('strategies.card.toggle', { category: strategy.category.name })"
                @update:model-value="onToggle"
            />
        </div>
        <AccordionContent :class="{ 'opacity-50': !strategy.enabled }">
            <template v-if="isVariation">
                <div
                    v-if="strategy.category.slug === 'pc'"
                    class="p-4 mb-4 text-sm rounded-lg bg-muted border border-border"
                    role="alert"
                >
                    <div class="flex items-center gap-2 font-medium">
                        <Info class="size-4 shrink-0" />
                        <span>Additional strategies</span>
                    </div>

                    <span class="block mt-4 font-medium">Macbook (incl. Pro, Air)</span>
                    <ul class="block mt-2 list-disc pl-4 text-muted-foreground">
                        <li>
                            2014, 2015, 2016, 2017 and screen 13"
                            <strong class="inline-block ml-2 text-foreground">x 1.45</strong>
                        </li>
                        <li>
                            2014, 2015, 2016, 2017 and screen 15" <strong class="inline-block ml-2 text-foreground">x 0.6</strong>
                        </li>
                        <li>2018, 2019 (all screens) <strong class="inline-block ml-2 text-foreground">x 1.4</strong></li>
                        <li>2020, 2022 (all screens) <strong class="inline-block ml-2 text-foreground">x 1.2</strong></li>
                    </ul>

                    <span class="block mt-4 font-medium">iMac</span>
                    <ul class="block mt-2 list-disc pl-4 text-muted-foreground">
                        <li>2014, 2015 and screen 21" <strong class="inline-block ml-2 text-foreground">x 1.25</strong></li>
                        <li>2017 and screen 21" <strong class="inline-block ml-2 text-foreground">x 0.8</strong></li>
                        <li>2014 and screen 27" <strong class="inline-block ml-2 text-foreground">x 0.7</strong></li>
                        <li>2019, 2020 (all screens) <strong class="inline-block ml-2 text-foreground">x 0.8</strong></li>
                    </ul>
                </div>

                <form @submit.prevent="onSubmit">
                    <div class="leading-10 text-muted-foreground">
                        {{ t('strategies.buyback.card.firstSentence') }}

                        <div class="inline-flex w-16 mx-4 relative">
                            <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground"
                                >%</span
                            >
                            <Input
                                v-model="absPricePositioning"
                                type="text"
                                class="pr-8"
                            />
                        </div>

                        <div class="inline-flex w-32 mx-4">
                            <Select v-model="greaterOrLesser">
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="greater">{{ t('strategies.buyback.card.greater') }}</SelectItem>
                                    <SelectItem value="lesser">{{ t('strategies.buyback.card.lesser') }}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {{ t('strategies.buyback.card.secondSentence') }}

                        <div class="inline-flex w-48 mx-4">
                            <Select v-model="roundingType">
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="fixed">{{ t('strategies.buyback.card.roundedBy') }}</SelectItem>
                                    <SelectItem value="exact_amount">{{ t('strategies.buyback.card.noRounding') }}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div v-if="roundingType === 'fixed'" class="inline-flex w-20 mx-4 relative">
                            <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground">{{
                                user.extra?.catalog_currency_symbol
                            }}</span>
                            <Input
                                v-model="strategy.rounding_value"
                                type="text"
                                class="pr-8"
                            />
                        </div>
                    </div>
                    <p class="mt-1 text-sm text-muted-foreground">
                        {{ t(roundingType === 'fixed' ? 'strategies.buyback.card.roundingHint' : 'strategies.buyback.card.noRoundingHint') }}
                    </p>

                    <div v-if="hasBrokenParts" class="mt-6 border-t border-border pt-4">
                        <h3 class="font-medium">{{ t('strategies.buyback.card.brokenParts.title') }}</h3>
                        <p class="mt-1 text-sm text-muted-foreground">
                            {{ t('strategies.buyback.card.brokenParts.description') }}
                        </p>

                        <div class="mt-4 grid grid-cols-[1fr_auto_5rem_6rem] items-center gap-x-4 gap-y-2 text-sm">
                            <span class="text-muted-foreground">{{ t('strategies.buyback.card.brokenParts.part') }}</span>
                            <span class="text-muted-foreground">{{ t('strategies.buyback.card.brokenParts.enabled') }}</span>
                            <span class="text-muted-foreground">{{ t('strategies.buyback.card.brokenParts.percentage') }}</span>
                            <span class="text-muted-foreground">{{ t('strategies.buyback.card.brokenParts.amount') }}</span>

                            <template v-for="(deduction, part) in brokenParts.deductions" :key="part">
                                <span :class="{ 'text-muted-foreground': !deduction.enabled }">
                                    {{ t(`strategies.buyback.card.brokenParts.parts.${part}`) }}
                                </span>
                                <Switch
                                    v-model="deduction.enabled"
                                    :aria-label="t(`strategies.buyback.card.brokenParts.parts.${part}`)"
                                />
                                <div class="relative">
                                    <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground"
                                        >%</span
                                    >
                                    <Input v-model="deduction.percentage" type="text" class="pr-8" :disabled="!deduction.enabled" />
                                </div>
                                <div class="relative">
                                    <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground">{{
                                        user.extra?.catalog_currency_symbol
                                    }}</span>
                                    <Input v-model="deduction.amount" type="text" class="pr-8" :disabled="!deduction.enabled" />
                                </div>
                            </template>
                        </div>

                        <div class="mt-6 grid gap-4 sm:grid-cols-2">
                            <label class="flex flex-col gap-1 text-sm">
                                <span class="font-medium">{{ t('strategies.buyback.card.brokenParts.minimumTradeIn') }}</span>
                                <div class="relative w-32">
                                    <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground">{{
                                        user.extra?.catalog_currency_symbol
                                    }}</span>
                                    <Input v-model="minimumTradeIn" type="text" class="pr-8" />
                                </div>
                                <span class="text-muted-foreground">{{ t('strategies.buyback.card.brokenParts.minimumTradeInHint') }}</span>
                            </label>
                            <label class="flex flex-col gap-1 text-sm">
                                <span class="font-medium">{{ t('strategies.buyback.card.brokenParts.zeroOutThreshold') }}</span>
                                <div class="relative w-32">
                                    <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground">{{
                                        user.extra?.catalog_currency_symbol
                                    }}</span>
                                    <Input v-model="zeroOutThreshold" type="text" class="pr-8" />
                                </div>
                                <span class="text-muted-foreground">{{ t('strategies.buyback.card.brokenParts.zeroOutThresholdHint') }}</span>
                            </label>
                        </div>
                    </div>

                    <div class="flex mt-4">
                        <Button type="submit">
                            {{ t('strategies.buyback.card.apply') }}
                        </Button>
                    </div>
                </form>
            </template>

            <ul v-else-if="strategy.type === 'unieuro'" class="list-disc pl-4 text-sm text-muted-foreground">
                <li>{{ t('strategies.buyback.unieuro.ruleFound') }}</li>
                <li>{{ t('strategies.buyback.unieuro.ruleNotFound') }}</li>
            </ul>
        </AccordionContent>
    </AccordionItem>
</template>
